import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  BarChart3,
  Building2,
  Camera,
  ClipboardList,
  Home,
  LogOut,
  Menu,
  Mic,
  Phone,
  Plus,
  ShieldCheck,
  Tag,
  User,
  Users,
  Settings,
  X,
  CloudOff,
  UploadCloud,
} from "lucide-react";
import { api } from "../lib/api.ts";
import { hasRole, useAuth } from "../lib/auth.tsx";
import { fmtDateTime, fmtDuration, fmtTime } from "../lib/format.ts";
import { useRecorder, useUploads } from "../recording/hooks.ts";
import { recorder } from "../recording/recorder.ts";
import { MODULES } from "../../../shared/modules.ts";
import type { ModuleId, Notification } from "../../../shared/types.ts";
import { Banner, Confirm, Loading, cx } from "./ui.tsx";

const MODULE_ICON = { mic: Mic, cam: Camera, tag: Tag, shield: ShieldCheck, chart: BarChart3, phone: Phone } as const;

export function moduleHref(id: ModuleId, connected: boolean): string {
  if (!connected) return `/modules/${id}`;
  if (id === "voice") return "/visits";
  if (id === "coding") return "/coding";
  return `/modules/${id}`;
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useAuth();
  const clinic = me?.clinic;
  const connected = clinic?.modules ?? [];
  const doctor = hasRole(me, "doctor");
  const admin = hasRole(me, "clinic_admin");
  const aisol = hasRole(me, "aisol_admin");
  const link = ({ isActive }: { isActive: boolean }) => cx("nav-link", isActive && "nav-link-active");
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-3" onClick={onNavigate}>
      {doctor && clinic && (
        <Link to="/visits/new" className="btn-violet w-full mb-3 lg:hidden">
          <Plus className="w-5 h-5" /> Новый приём
        </Link>
      )}
      <NavLink to="/" end className={link}>
        <Home className="w-[18px] h-[18px]" /> Главная
      </NavLink>
      {clinic && (
        <>
          <div className="nav-section">Модули платформы</div>
          {MODULES.map((m) => {
            const Icon = MODULE_ICON[m.icon];
            const on = connected.includes(m.id as ModuleId);
            return (
              <NavLink key={m.id} to={moduleHref(m.id as ModuleId, on)} className={({ isActive }) => cx("nav-link", isActive && "nav-link-active")}>
                <Icon className="w-[18px] h-[18px] shrink-0" />
                <span className="leading-tight">{m.title}</span>
              </NavLink>
            );
          })}
          <div className="nav-section">Платформа</div>
          <NavLink to="/patients" className={link}>
            <Users className="w-[18px] h-[18px]" /> Пациенты
          </NavLink>
          <NavLink to="/journal" className={link}>
            <ClipboardList className="w-[18px] h-[18px]" /> Журнал
          </NavLink>
          <NavLink to="/stats" className={link}>
            <BarChart3 className="w-[18px] h-[18px]" /> Статистика
          </NavLink>
          {admin && (
            <NavLink to="/admin" className={link}>
              <Settings className="w-[18px] h-[18px]" /> Администрирование
            </NavLink>
          )}
        </>
      )}
      {aisol && (
        <>
          <div className="nav-section">AiSol</div>
          <NavLink to="/aisol" className={link}>
            <Building2 className="w-[18px] h-[18px]" /> Клиники и настройки
          </NavLink>
          {!clinic && (
            <>
              <NavLink to="/journal" className={link}>
                <ClipboardList className="w-[18px] h-[18px]" /> Журнал
              </NavLink>
              <NavLink to="/stats" className={link}>
                <BarChart3 className="w-[18px] h-[18px]" /> Статистика
              </NavLink>
            </>
          )}
        </>
      )}
      <NavLink to="/profile" className={link}>
        <User className="w-[18px] h-[18px]" /> Профиль
      </NavLink>
    </nav>
  );
}

function Notifications() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useAuth();
  const q = useQuery({ queryKey: ["notifications"], queryFn: () => api<{ notifications: Notification[]; unread: number }>("/notifications"), refetchInterval: 30_000 });
  const unread = q.data?.unread ?? 0;
  return (
    <div className="relative">
      <button className="relative p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-mutedt hover:text-ink" onClick={() => setOpen((v) => !v)} aria-label="Уведомления">
        <Bell className="w-5 h-5" />
        {unread > 0 && <span className="absolute top-1.5 right-1.5 bg-err text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-50 w-[min(92vw,360px)] card p-2 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="font-semibold text-[14px]">Уведомления</span>
              {unread > 0 && (
                <button
                  className="btn-link text-[12px]"
                  onClick={async () => {
                    await api("/notifications/read", { body: {} });
                    void qc.invalidateQueries({ queryKey: ["notifications"] });
                  }}
                >
                  Прочитать все
                </button>
              )}
            </div>
            {(q.data?.notifications ?? []).length === 0 && <div className="text-[13px] text-mutedt px-2 py-4">Новых уведомлений нет</div>}
            {(q.data?.notifications ?? []).map((n) => (
              <button
                key={n.id}
                className={cx("w-full text-left px-2 py-2 rounded-lg hover:bg-soft text-[13px]", !n.read && "font-semibold")}
                onClick={async () => {
                  setOpen(false);
                  if (!n.read) await api("/notifications/read", { body: { ids: [n.id] } }).catch(() => {});
                  void qc.invalidateQueries({ queryKey: ["notifications"] });
                  if (n.visitId) navigate(`/visits/${n.visitId}`);
                }}
              >
                <div>{n.text}</div>
                <div className="text-[11px] text-mutedt font-normal mt-0.5">{fmtDateTime(n.at, me?.clinic?.timezone)}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** «Идёт запись» on any screen, interrupted recording, uploads waiting for the network (ТЗ §8.1, §8.3, §8.5). */
function GlobalBanners() {
  const rec = useRecorder();
  const up = useUploads();
  const loc = useLocation();
  const onRecordPage = rec.visitId && loc.pathname === `/visits/${rec.visitId}/record`;
  const otherPending = up.recordings.filter((r) => r.visitId !== rec.visitId && r.pending > 0);
  return (
    <div className="space-y-2 empty:hidden mb-3 no-print">
      {rec.status === "recording" && !onRecordPage && (
        <Link to={`/visits/${rec.visitId}/record`} className="block">
          <Banner kind="err" icon={<span className="rec-dot" />}>
            <b>Идёт запись</b> · {rec.patientName ?? "приём"} · {fmtDuration(rec.elapsedMs)} — открыть экран записи
          </Banner>
        </Link>
      )}
      {(rec.status === "paused" || rec.status === "autopaused") && !onRecordPage && (
        <Link to={`/visits/${rec.visitId}/record`} className="block">
          <Banner kind="warn">
            <b>{rec.status === "autopaused" ? "Запись прервана. Продолжить?" : "Запись на паузе"}</b> · {rec.patientName ?? "приём"} · {fmtDuration(rec.elapsedMs)}
          </Banner>
        </Link>
      )}
      {up.needsLogin && <Banner kind="warn">Войдите снова, чтобы догрузить запись приёма — она сохранена на телефоне</Banner>}
      {otherPending.map((r) => (
        <Banner key={`${r.visitId}${r.recordingNo}`} kind={up.online ? "info" : "warn"} icon={up.online ? <UploadCloud className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}>
          {up.online ? (
            <>Догружаем запись приёма от {fmtTime(r.startedAt)}{r.patientName ? ` · ${r.patientName}` : ""} — осталось {r.pending} из {r.produced}</>
          ) : (
            <>Ждёт сети · {Math.max(1, Math.round((r.pending * 10) / 60))} мин записи на телефоне{r.patientName ? ` · ${r.patientName}` : ""}</>
          )}
        </Banner>
      ))}
    </div>
  );
}

export function Layout() {
  const { me, logout } = useAuth();
  const up = useUploads();
  const rec = useRecorder();
  const [drawer, setDrawer] = useState(false);
  const [askLogout, setAskLogout] = useState(false);
  const loc = useLocation();
  const recordScreen = /\/visits\/[^/]+\/record$/.test(loc.pathname);
  useEffect(() => setDrawer(false), [loc.pathname]);
  const pending = up.pendingSegments + (rec.status !== "idle" ? 1 : 0);

  const doLogout = async () => {
    if (rec.status !== "idle") await recorder.finish("doctor");
    await logout(up.pendingSegments);
  };

  const sidebarInner = (
    <>
      <div className="px-5 py-4 flex items-start justify-between gap-2 border-b border-white/10">
        <Link to="/" className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-violet">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-bold tracking-wide text-white">AiSol Med</div>
            <div className="text-[9.5px] uppercase tracking-wider text-[#a99ef0]">платформа для клиник</div>
          </div>
        </Link>
        <button className="lg:hidden text-white/70 p-2 -mr-2 min-w-[44px] min-h-[44px]" onClick={() => setDrawer(false)} aria-label="Закрыть меню">
          <X className="w-5 h-5" />
        </button>
      </div>
      {me?.clinic && (
        <div className="px-5 py-3 border-b border-white/10">
          <div className="text-[9.5px] uppercase tracking-wider text-[#8f83e8]">Организация</div>
          <div className="text-[12.5px] font-semibold text-white mt-0.5 leading-tight">{me.clinic.name}</div>
        </div>
      )}
      <Sidebar onNavigate={() => setDrawer(false)} />
      <div className="px-4 py-3 border-t border-white/10 text-[12px] text-[#a99ef0]">
        <div className="truncate text-white/90 font-semibold">{me?.user.fullName}</div>
        <div className="truncate">{me?.user.email}</div>
        <button className="mt-2 inline-flex items-center gap-2 text-white/80 hover:text-white min-h-[40px]" onClick={() => (pending > 0 ? setAskLogout(true) : void doLogout())}>
          <LogOut className="w-4 h-4" /> Выйти
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-[100dvh]">
      <aside className="hidden lg:flex w-64 flex-col shrink-0 bg-indigo-d text-violet-soft no-print">{sidebarInner}</aside>
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-[70] flex no-print">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <aside className="relative w-[85vw] max-w-[300px] flex flex-col bg-indigo-d text-violet-soft h-full">{sidebarInner}</aside>
        </div>
      )}
      <main className="flex-1 min-w-0 overflow-y-auto">
        {!recordScreen && (
          <header className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-line no-print">
            <div className="px-3 sm:px-6 h-14 flex items-center gap-2">
              <button className="lg:hidden p-2 -ml-1 min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setDrawer(true)} aria-label="Открыть меню">
                <Menu className="w-6 h-6" />
              </button>
              <Link to="/" className="lg:hidden font-bold text-[15px]">
                AiSol Med
              </Link>
              <div className="flex-1" />
              {!up.online && (
                <span className="pill-warn mr-1">
                  <CloudOff className="w-3 h-3" /> нет сети
                </span>
              )}
              {hasRole(me, "doctor") && me?.clinic && (
                <Link to="/visits/new" className="btn-violet btn-sm hidden sm:inline-flex">
                  <Plus className="w-4 h-4" /> Новый приём
                </Link>
              )}
              <Notifications />
            </div>
          </header>
        )}
        <div className={cx(recordScreen ? "" : "p-3 sm:p-5 lg:p-7 max-w-[1400px] mx-auto pb-[max(24px,env(safe-area-inset-bottom))]")}>
          {!recordScreen && <GlobalBanners />}
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <Confirm
        open={askLogout}
        title="Выйти из платформы?"
        text="Есть недогруженная запись приёма. Выйти всё равно? Запись останется на телефоне и догрузится, когда вы снова войдёте."
        confirmText="Выйти всё равно"
        danger
        onConfirm={doLogout}
        onClose={() => setAskLogout(false)}
      />
    </div>
  );
}
